import { Outlet, Link as RouterLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemIcon,
  Tooltip,
} from "@mui/material";
import { useUser } from "./UserContext";
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  ManageAccounts as ManageAccountsIcon,
  AccountTree as AccountTreeIcon,
  Create as CreateIcon,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import AssistantIcon from "@mui/icons-material/Assistant";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PsychologyIcon from "@mui/icons-material/Psychology";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AdminPanelSettings from "@mui/icons-material/AdminPanelSettings";
import FilterListIcon from "@mui/icons-material/FilterList";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AutoAwesomeMosaicIcon from "@mui/icons-material/AutoAwesomeMosaic";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import KeyIcon from "@mui/icons-material/Key";
import LockIcon from "@mui/icons-material/Lock";
import AddModeratorIcon from "@mui/icons-material/AddModerator";
import SpeedIcon from "@mui/icons-material/Speed";
import MemoryIcon from "@mui/icons-material/Memory";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChangePasswordDialog from "./ChangePasswordDialog";
import ForcePasswordChangeOverlay from "./ForcePasswordChangeOverlay";
import CasperBubble from "./Casper/CasperBubble.jsx";
import { useFeatureFlags } from "../context/FeatureFlagContext";
import { useThemeMode } from "../ThemeModeContext";
import { useTenantBranding } from "../context/TenantBrandingContext";
import {
  getBrandAppBarColor,
  isDefaultNextMocaBranding,
  isDefaultNextMocaLogo,
} from "../branding";
import { APP_LAYOUT } from "../constants";
import { PERMISSIONS } from "../permissions";
import { canViewPlatformTelemetry } from "../rbacUiPolicy";

const drawerWidth = 270;
const collapsedDrawerWidth = 72;
const appBarBaseHeight = APP_LAYOUT.appBarHeight;
const defaultAppBarLogoMaxHeight = 92;
const RouterLinkComponent = /** @type {any} */ (RouterLink);

function routeMatchesNavItem(pathname, itemPath) {
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

export default function Layout() {
  const { user, permissions, logout, requiresPasswordChange, clearPasswordChangeFlag, can, canAny } =
    useUser();
  const { flags, refreshFlags } = useFeatureFlags();
  const { mode, toggleMode } = useThemeMode();
  const { branding, resolveBrandAssetUrl } = useTenantBranding();
  const location = useLocation();
  const appBarRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [profilePanelOpen, setProfilePanelOpen] = useState(false);
  const [appBarHeight, setAppBarHeight] = useState(0);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] =
    useState(false);
  const canUseCreateTool = flags.enableCreateTool && canAny([PERMISSIONS.TOOLS_CREATE, PERMISSIONS.TOOLS_CONFIGURE]);
  const canUsePlatformTelemetry = canViewPlatformTelemetry(permissions);

  // Measure AppBar height on mount and whenever the banner geometry changes.
  useEffect(() => {
    const measureAppBarHeight = () => {
      if (appBarRef.current) {
        const nextHeight = Math.ceil(appBarRef.current.getBoundingClientRect().height);
        setAppBarHeight((currentHeight) => (
          currentHeight === nextHeight ? currentHeight : nextHeight
        ));
      }
    };

    measureAppBarHeight();

    const resizeObserver =
      typeof ResizeObserver !== "undefined" && appBarRef.current
        ? new ResizeObserver(measureAppBarHeight)
        : null;
    if (resizeObserver && appBarRef.current) {
      resizeObserver.observe(appBarRef.current);
    }

    window.addEventListener("resize", measureAppBarHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measureAppBarHeight);
    };
  }, [drawerOpen, branding.brandName, branding.logoUrl, branding.showPoweredByNextMoca]);

  useEffect(() => {
    if (user) {
      refreshFlags();
    }
  }, [user, refreshFlags]);

  const getNavSections = useCallback(() => {
    const casperSection = {
      label: "Casper",
      to: "/casper",
      icon: <AutoAwesomeIcon />,
    };
    if (
      user &&
      !canAny([
        PERMISSIONS.AGENTS_READ,
        PERMISSIONS.WORKFLOWS_READ,
        PERMISSIONS.TOOLS_READ,
        PERMISSIONS.KNOWLEDGE_READ,
        PERMISSIONS.USERS_READ,
        PERMISSIONS.USERS_MANAGE,
        PERMISSIONS.ROLES_READ,
        PERMISSIONS.AUDIT_READ,
      ])
    ) {
      return [casperSection];
    }
    const grcItems = [
      ...(can(PERMISSIONS.AUDIT_READ)
        ? [
            {
              label: "Audit Logs",
              to: "/audit-logs",
              icon: <FilterListIcon />,
            },
          ]
        : []),
      ...(canUsePlatformTelemetry
        ? [
            {
              label: "Needlepath",
              to: "/needlepath",
              icon: <SpeedIcon />,
            },
            {
              label: "Mnemo",
              to: "/mnemo",
              icon: <MemoryIcon />,
            },
          ]
        : []),
    ];
    return [
      casperSection,
      {
        label: "Dashboard",
        to: "/dashboard",
        icon: <DashboardIcon />,
      },
      ...(canAny([PERMISSIONS.AGENTS_READ, PERMISSIONS.AGENTS_CREATE])
        ? [
            {
              label: "Agents",
              icon: <AssistantIcon />,
              items: [
                ...(can(PERMISSIONS.AGENTS_READ)
                  ? [{ label: "Catalog", to: "/agents", icon: <MenuBookIcon /> }]
                  : []),
                ...(can(PERMISSIONS.AGENTS_CREATE)
                  ? [
                      {
                        label: "Agent Creator",
                        to: "/agent/conversational",
                        icon: <SmartToyIcon />,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      ...(canAny([PERMISSIONS.TOOLS_READ, PERMISSIONS.TOOLS_CREATE, PERMISSIONS.TOOLS_CONFIGURE])
        ? [
            {
              label: "Tools",
              icon: <AutoAwesomeMosaicIcon />,
              items: [
                ...(can(PERMISSIONS.TOOLS_READ)
                  ? [{ label: "Catalog", to: "/tools", icon: <StorefrontIcon /> }]
                  : []),
                ...(canUseCreateTool
                  ? [
                      {
                        label: "Tool Forge",
                        to: "/tool-forge",
                        icon: <AutoAwesomeMosaicIcon />,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      ...(canAny([PERMISSIONS.KNOWLEDGE_READ, PERMISSIONS.KNOWLEDGE_CREATE])
        ? [
            {
              label: "Knowledge Spaces",
              icon: <PsychologyIcon />,
              items: [
                ...(can(PERMISSIONS.KNOWLEDGE_READ)
                  ? [{ label: "Collections", to: "/kb", icon: <MenuBookIcon /> }]
                  : []),
                ...(can(PERMISSIONS.KNOWLEDGE_CREATE)
                  ? [
                      {
                        label: "Builder",
                        to: "/kb/document-indexer",
                        icon: <LibraryAddIcon />,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      ...(canAny([PERMISSIONS.WORKFLOWS_READ, PERMISSIONS.WORKFLOWS_CREATE, PERMISSIONS.WORKFLOWS_APPROVE]) &&
      flags.enableWorkflows
        ? [
            {
              label: "Workflows",
              icon: <AccountTreeIcon />,
              items: [
                ...(can(PERMISSIONS.WORKFLOWS_READ)
                  ? [{ label: "Catalog", to: "/workflows", icon: <MenuBookIcon /> }]
                  : []),
                ...(can(PERMISSIONS.WORKFLOWS_CREATE)
                  ? [
                      {
                        label: "Workflow Generator",
                        to: "/workflow/generate",
                        icon: <CreateIcon />,
                      },
                    ]
                  : []),
                ...(can(PERMISSIONS.WORKFLOWS_APPROVE)
                  ? [
                      {
                        label: "Approvals",
                        to: "/workflow/approvals",
                        icon: <AssistantIcon />,
                      },
                    ]
                  : []),
                // { label: "Workflow Orchestrator", to: "/workflow/orchestrator", icon: <AddCircleIcon /> },
              ],
            },
          ]
        : []),
      ...(canAny([PERMISSIONS.USERS_READ, PERMISSIONS.USERS_MANAGE, PERMISSIONS.ROLES_READ])
        ? [
            {
              label: "Users and Roles",
              icon: <ManageAccountsIcon />,
              items: [
                ...(can(PERMISSIONS.USERS_READ)
                  ? [{ label: "List Users", to: "/users", icon: <PeopleIcon /> }]
                  : []),
                ...(can(PERMISSIONS.USERS_MANAGE)
                  ? [{ label: "Add User", to: "/add-user", icon: <PersonAddIcon /> }]
                  : []),
                ...(can(PERMISSIONS.ROLES_READ)
                  ? [{ label: "Roles", to: "/roles", icon: <AddModeratorIcon /> }]
                  : []),
              ],
            },
          ]
        : []),
      ...(grcItems.length > 0
        ? [
            {
              label: "GRC Operations",
              icon: <AdminPanelSettings />,
              items: grcItems,
            },
          ]
        : []),
    ];
  }, [user, flags.enableWorkflows, canUseCreateTool, canUsePlatformTelemetry, can, canAny]);

  const navSections = useMemo(() => getNavSections(), [getNavSections]);
  const activeSectionLabel = navSections.find((section) =>
    section.items?.some((item) => routeMatchesNavItem(location.pathname, item.to)),
  )?.label;
  const isCasperRoute = location.pathname === "/casper";
  const isAgentRunRoute = /^\/agent\/[^/]+\/chatAgent$/.test(location.pathname);
  const isImmersiveRoute = isCasperRoute || isAgentRunRoute;
  const appBarOffset = appBarHeight || appBarBaseHeight;
  const currentDrawerWidth = drawerOpen ? drawerWidth : collapsedDrawerWidth;
  const usesDefaultNextMocaLogo = isDefaultNextMocaLogo(branding.logoUrl);
  const shouldUseDefaultNextMocaLogo = isDefaultNextMocaBranding(branding);
  const usesCustomNameWithDefaultLogo = usesDefaultNextMocaLogo && !shouldUseDefaultNextMocaLogo;
  const expandedLogoUrl = resolveBrandAssetUrl(branding.logoUrl);
  const shouldRenderExpandedLogo = Boolean(expandedLogoUrl) && !usesCustomNameWithDefaultLogo;
  const compactLogoUrl = shouldUseDefaultNextMocaLogo
    ? "/nextmoca-logo.png"
    : usesCustomNameWithDefaultLogo
      ? null
      : resolveBrandAssetUrl(branding.faviconUrl || branding.logoUrl);
  const appBarBackground = getBrandAppBarColor(branding);
  const appBarPoweredByAttribution = branding.showPoweredByNextMoca ? (
    <Typography
      variant="caption"
      sx={{
        color: "rgba(255,255,255,0.58)",
        fontSize: "0.72rem",
        lineHeight: 1,
        mt: 0.75,
      }}
    >
      powered by Next Moca
    </Typography>
  ) : null;
  const [expanded, setExpanded] = useState(
    () => new Set([navSections[0]?.label, activeSectionLabel].filter(Boolean)),
  );

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(panel);
      } else {
        newSet.delete(panel);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (!activeSectionLabel) return;
    setExpanded((current) => {
      if (current.has(activeSectionLabel)) return current;
      const next = new Set(current);
      next.add(activeSectionLabel);
      return next;
    });
  }, [activeSectionLabel]);

  // Handle successful password change from overlay
  const handlePasswordChangeSuccess = () => {
    clearPasswordChangeFlag();
  };

  // Handle logout from overlay
  const handlePasswordChangeLogout = () => {
    logout();
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: isImmersiveRoute ? "100vh" : "auto",
        overflow: isImmersiveRoute ? "hidden" : "visible",
      }}
    >
      {/* Force Password Change Overlay */}
      {requiresPasswordChange && (
        <ForcePasswordChangeOverlay
          onSuccess={handlePasswordChangeSuccess}
          onLogout={handlePasswordChangeLogout}
        />
      )}

      <AppBar
        ref={appBarRef}
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        style={{ backgroundColor: appBarBackground }}
      >
        <Toolbar
          sx={{
            position: "relative",
            minHeight: `${appBarBaseHeight}px !important`,
            height: appBarBaseHeight,
            maxHeight: appBarBaseHeight,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              width: drawerOpen && shouldUseDefaultNextMocaLogo ? 400 : currentDrawerWidth,
              height: appBarBaseHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: (theme) =>
                theme.transitions.create("width", {
                  duration: theme.transitions.duration.shortest,
                }),
            }}
          >
            {drawerOpen ? (
              shouldRenderExpandedLogo ? (
                <Box
                  sx={{
                    display: "inline-flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: shouldUseDefaultNextMocaLogo ? 400 : currentDrawerWidth,
                    maxWidth: "100%",
                    px: shouldUseDefaultNextMocaLogo ? 0 : 2,
                    overflow: shouldUseDefaultNextMocaLogo ? "visible" : "hidden",
                    lineHeight: 1,
                  }}
                >
                  <img
                    src={expandedLogoUrl}
                    alt={`${branding.brandName} logo`}
                    style={{
                      width: shouldUseDefaultNextMocaLogo ? "375px" : "100%",
                      height: "auto",
                      maxHeight: shouldUseDefaultNextMocaLogo
                        ? `${defaultAppBarLogoMaxHeight}px`
                        : branding.showPoweredByNextMoca
                          ? "42px"
                          : "58px",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                  {appBarPoweredByAttribution}
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.1, px: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#fff", letterSpacing: "0.02em" }}>
                    {branding.brandName}
                  </Typography>
                  {appBarPoweredByAttribution}
                </Box>
              )
            ) : (
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {compactLogoUrl ? (
                  <img
                    src={compactLogoUrl}
                    alt={branding.brandName}
                    style={{
                      width: "54px",
                      height: "54px",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                ) : (
                  <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: 18 }}>
                    {branding.brandName.slice(0, 1).toUpperCase()}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {user && (
            <>
              <Tooltip title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
                <IconButton
                  color="inherit"
                  aria-label={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                  onClick={toggleMode}
                  sx={{
                    mr: 0.5,
                    border: "1px solid rgba(255,255,255,0.22)",
                    bgcolor: "rgba(255,255,255,0.08)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
                  }}
                >
                  {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
              <IconButton
                color="inherit"
                onClick={() => setProfilePanelOpen(open => !open)}
                aria-label="Open account menu"
              >
                <SettingsIcon />
              </IconButton>
              <Button
                color="inherit"
                // onClick={() => setProfilePanelOpen(open => !open)}
                sx={{ ml: 1, textTransform: "none" }}
              >
                {user.username}
              </Button>
              <Drawer
                anchor="right"
                open={profilePanelOpen}
                onClose={() => setProfilePanelOpen(false)}
                PaperProps={{
                  sx: {
                    width: "27%",
                    top: `${appBarHeight}px`, // Dynamically position below the toolbar
                    // height: `calc(100% - ${appBarHeight}px)`,  // Adjust height to account for toolbar
                    height: "36%", // Adjust height to account for toolbar
                    pt: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {user.username}
                  </Typography>
                  <IconButton
                    onClick={() => setProfilePanelOpen(false)}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <List>
                  {/* TODO: Not implemented yet
                  <ListItem disablePadding>
                    <ListItemButton component={RouterLinkComponent} to="/profile" onClick={() => setProfilePanelOpen(false)}>
                      <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                        <AccountCircleIcon />
                      </ListItemIcon>
                      <ListItemText primary="Your Profile" />
                    </ListItemButton>
                  </ListItem> */}
                  {can(PERMISSIONS.CONFIG_FEATURE_FLAGS_MANAGE) && (
                    <ListItem disablePadding>
                      <ListItemButton
                        component={RouterLinkComponent}
                        to="/config"
                        onClick={() => setProfilePanelOpen(false)}
                      >
                        <ListItemIcon
                          sx={{ minWidth: 40, color: "primary.main" }}
                        >
                          <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText primary="Config" />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {can(PERMISSIONS.CONFIG_KEYS_MANAGE) && (
                    <ListItem disablePadding>
                      <ListItemButton
                        component={RouterLinkComponent}
                        to="/key-manager"
                        onClick={() => setProfilePanelOpen(false)}
                      >
                        <ListItemIcon
                          sx={{ minWidth: 40, color: "primary.main" }}
                        >
                          <KeyIcon />
                        </ListItemIcon>
                        <ListItemText primary="Key Manager" />
                      </ListItemButton>
                    </ListItem>
                  )}
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        setProfilePanelOpen(false);
                        setChangePasswordDialogOpen(true);
                      }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 40, color: "primary.main" }}
                      >
                        <LockIcon />
                      </ListItemIcon>
                      <ListItemText primary="Change Password" />
                    </ListItemButton>
                  </ListItem>
                  <Divider sx={{ my: 1 }} />
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        setProfilePanelOpen(false);
                        logout();
                      }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 40, color: "primary.main" }}
                      >
                        <LogoutIcon />
                      </ListItemIcon>
                      <ListItemText primary="Logout" />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Drawer>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={drawerOpen}
        PaperProps={{
          sx: {
            width: currentDrawerWidth,
            boxSizing: "border-box",
            transition: "width 0.2s",
            overflowX: "hidden",
            top: `${appBarOffset}px`,
            bottom: 0,
            height: `calc(100vh - ${appBarOffset}px)`,
            borderRight: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          },
        }}
        sx={{
          width: currentDrawerWidth,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: currentDrawerWidth,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              height: 58,
              display: "flex",
              alignItems: "center",
              justifyContent: drawerOpen ? "flex-end" : "center",
              px: drawerOpen ? 1.5 : 0,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Tooltip
              title={drawerOpen ? "Collapse navigation" : "Open sidebar"}
              placement="right"
            >
              <IconButton
                aria-label={drawerOpen ? "Collapse navigation" : "Open sidebar"}
                onClick={() => setDrawerOpen((open) => !open)}
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "14px",
                  color: "text.secondary",
                  bgcolor: drawerOpen ? "transparent" : "action.selected",
                  "&:hover": {
                    bgcolor: "action.hover",
                    color: "text.primary",
                  },
                }}
              >
                <ViewSidebarOutlinedIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              flex: 1,
              width: currentDrawerWidth,
              pt: drawerOpen ? 2 : 1.5,
              overflowX: "hidden",
              overflowY: "auto",
            }}
          >
            {drawerOpen ? (
            <Box sx={{ width: drawerWidth, pt: 1 }}>
              {navSections?.map((section) =>
                !section.items ? (
                  /* Top-level link — no accordion */
                  <ListItem
                    key={section.label}
                    disablePadding
                    sx={{ mb: 1.25 }}
                  >
                    <ListItemButton
                      component={RouterLinkComponent}
                      to={section.to}
                      selected={location.pathname === section.to}
                      sx={{
                        minHeight: 30,
                        pl: 2.5,
                        py: 0.75,
                        borderRadius: 1,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 3,
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={section.label}
                        primaryTypographyProps={{ fontWeight: "bold" }}
                      />
                    </ListItemButton>
                  </ListItem>
                ) : (
                  /* Accordion for sections with children */
                  <Accordion
                    disableGutters
                    key={section.label}
                    expanded={expanded.has(section.label) && drawerOpen}
                    onChange={handleAccordionChange(section.label)}
                    sx={{
                      boxShadow: "none",
                      background: "transparent",
                      "&:before": { display: "none" },
                      mb: 1.25,
                    }}
                    disabled={!drawerOpen}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`${section.label}-content`}
                      id={`${section.label}-header`}
                      sx={{
                        minHeight: 30,
                        pl: 2.5,
                        borderRadius: 1,
                        bgcolor:
                          section.items?.some((i) => routeMatchesNavItem(location.pathname, i.to)) &&
                          !expanded.has(section.label)
                            ? "action.selected"
                            : "transparent",
                        "& .MuiAccordionSummary-content": {
                          my: 0.5,
                          alignItems: "center",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 3,
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={section.label}
                        primaryTypographyProps={{ fontWeight: "bold" }}
                      />
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0, pt: 0 }}>
                      <List dense sx={{ py: 0 }}>
                        {section.items?.map((item) => (
                          <ListItem
                            key={item.to}
                            disablePadding
                            sx={{ display: "block", pl: 6, py: 0 }}
                          >
                            <ListItemButton
                              component={RouterLinkComponent}
                              to={item.to}
                              selected={location.pathname === item.to}
                              sx={{
                                minHeight: 30,
                                justifyContent: drawerOpen
                                  ? "initial"
                                  : "center",
                                px: 2.5,
                                py: 0.5,
                                borderRadius: 1,
                                "&.Mui-selected": {
                                  bgcolor: "action.selected",
                                },
                                "&.Mui-selected:hover": {
                                  bgcolor: "action.selected",
                                },
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 0,
                                  mr: 1.5,
                                  color: "primary.main",
                                }}
                              >
                                {item.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={drawerOpen ? item.label : ""}
                                primaryTypographyProps={{
                                  fontWeight: "bold",
                                  fontSize: "0.80rem",
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ),
              )}
            </Box>
          ) : (
            <List
              dense
              sx={{
                px: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                alignItems: "center",
              }}
            >
              {navSections?.map((section) => {
                const sectionActive = section.to
                  ? location.pathname === section.to
                  : section.items?.some((item) => routeMatchesNavItem(location.pathname, item.to));
                const collapsedButton = (
                  <ListItemButton
                    component={section.to ? RouterLinkComponent : "button"}
                    to={section.to || undefined}
                    selected={sectionActive}
                    onClick={
                      section.items
                        ? () => {
                            setDrawerOpen(true);
                            setExpanded((current) => {
                              const next = new Set(current);
                              next.add(section.label);
                              return next;
                            });
                          }
                        : undefined
                    }
                    sx={{
                      width: 48,
                      height: 48,
                      minHeight: 48,
                      borderRadius: "14px",
                      justifyContent: "center",
                      px: 0,
                      color: sectionActive ? "primary.main" : "text.secondary",
                      "&.Mui-selected": {
                        bgcolor: "action.selected",
                      },
                      "&.Mui-selected:hover": {
                        bgcolor: "action.selected",
                      },
                      "&:hover": {
                        bgcolor: "action.hover",
                        color: "primary.main",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        color: "inherit",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {section.icon}
                    </ListItemIcon>
                  </ListItemButton>
                );
                return (
                  <Tooltip title={section.label} placement="right" key={section.label}>
                    <ListItem disablePadding sx={{ display: "block", width: 48 }}>
                      {collapsedButton}
                    </ListItem>
                  </Tooltip>
                );
              })}
            </List>
            )}
          </Box>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={
          isCasperRoute
            ? {
                flexGrow: 1,
                mt: `${appBarOffset}px`,
                height: `calc(100vh - ${appBarOffset}px)`,
                minWidth: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.default",
                color: "text.primary",
              }
            : isAgentRunRoute
              ? {
                  flexGrow: 1,
                  pt: 9,
                  pr: 3,
                  pb: 3,
                  pl: 3,
                  mt: 3,
                  height: (theme) => `calc(100vh - ${theme.spacing(3)})`,
                  boxSizing: "border-box",
                  minWidth: 0,
                  minHeight: 0,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "background.default",
                  color: "text.primary",
                }
            : {
                flexGrow: 1,
                pt: 9,
                pr: 3,
                pb: 3,
                pl: 3,
                mt: 3,
                minHeight: "100vh",
                bgcolor: "background.default",
                color: "text.primary",
              }
        }
        // style={{border: "solid 2px red"}}
      >
        {/* <Container maxWidth="lg" style={{border: "solid 2px red"}}> */}

        <Outlet />
        {/* </Container> */}
      </Box>
      <CasperBubble />

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={changePasswordDialogOpen}
        onClose={() => setChangePasswordDialogOpen(false)}
      />
    </Box>
  );
}
